/**
 * Shared evidence-tag helpers for structured artifacts. HUEM tools annotate
 * fields with an evidence tag on the label line — `*(Grounded)*`, `*(Assumed)*`
 * or `*(Inferred)*` — which drives both the tag badge and the derived score /
 * gaps count on cards whose markdown carries no explicit number (e.g. JTBD,
 * whose contract forbids a score in the artifact).
 */

export type EvidenceTag = 'Grounded' | 'Assumed' | 'Inferred';

/** Confidence weight per tag — feeds the derived score. */
const WEIGHT: Record<EvidenceTag, number> = {
  Grounded: 1,
  Assumed: 0,
  Inferred: 0.5,
};

/** Normalise a raw tag string (from `*(...)*`) to an EvidenceTag, or null. */
export const parseEvidenceTag = (raw: string | null | undefined): EvidenceTag | null => {
  if (!raw) return null;
  const t = raw.trim().toLowerCase();
  if (t.startsWith('ground')) return 'Grounded';
  if (t.startsWith('assum')) return 'Assumed';
  if (t.startsWith('infer')) return 'Inferred';
  return null;
};

/** Minimal shape any evidence-tagged field satisfies. */
export interface Taggable {
  tag: EvidenceTag | null;
}

/**
 * Derive a 0–100 confidence score from a set of tagged fields
 * (Grounded = full, Inferred = half, Assumed / untagged = none). Used when the
 * artifact markdown carries no explicit score.
 */
export const weightedScore = (fields: Taggable[]): number => {
  if (!fields.length) return 0;
  const sum = fields.reduce((n, f) => n + (f.tag ? WEIGHT[f.tag] : 0), 0);
  return Math.round((sum / fields.length) * 100);
};

/** Count fields that are not yet Grounded — the "N gaps to fulfil" figure. */
export const gapsCount = (fields: Taggable[]): number =>
  fields.reduce((n, f) => n + (f.tag === 'Grounded' ? 0 : 1), 0);
